import { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, RefreshCw, Send, ExternalLink, Plus, Copy, Check, StickyNote, Ban } from 'lucide-react';
import moment from 'moment';
import toast from 'react-hot-toast';
import PageWrapper from '../../components/layout/PageWrapper';
import { Badge, Button, Card, Input, Spinner, Table, Modal } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { authGet, authPost, SessionExpiredError, getApiUrl } from '../../utils/api';

const STATUS_LABELS = { sent: 'Sent', not_sent: 'Not Sent', draft: 'Draft', signed: 'Signed', expired: 'Expired', cancelled: 'Cancelled' };
const STATUS_BADGE = { sent: 'sent', not_sent: 'expired', draft: 'default', signed: 'signed', expired: 'expired', cancelled: 'default' };
const FORM_TYPE_LABELS = { general: 'General', jg: 'JG' };
// Active, pre-signed, non-cancelled agreements — the ones that still have a usable link.
// Resend, Open form, and Cancel are all available for exactly this set.
const ACTIVE_STATUSES = ['sent', 'not_sent', 'draft', 'expired'];
const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'sent', label: 'Sent' },
    { key: 'not_sent', label: 'Not Sent' },
    { key: 'draft', label: 'Draft' },
    { key: 'signed', label: 'Signed' },
    { key: 'expired', label: 'Expired' },
    { key: 'cancelled', label: 'Cancelled' },
];

const ResendModal = ({ agreement, onConfirm, onClose }) => (
    <Modal isOpen={!!agreement} onClose={onClose} title="Resend Agreement Link" size="sm"
        footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={onConfirm}>Resend Link</Button></>}>
        {agreement && (
            <p className="text-sm text-gray-600">
                Resend the agreement link to{' '}
                <span className="font-semibold text-gray-900">{agreement.advisorFirstName} {agreement.advisorLastName}</span>{' '}
                at <span className="font-semibold text-gray-900">{agreement.advisorEmail}</span>?
                <br /><br />
                A new secure link (valid 2 weeks) will be sent and the previous link will be invalidated.
            </p>
        )}
    </Modal>
);

const CancelModal = ({ agreement, onConfirm, onClose }) => (
    <Modal isOpen={!!agreement} onClose={onClose} title="Cancel Agreement" size="sm"
        footer={<><Button variant="secondary" onClick={onClose}>Keep Agreement</Button><Button variant="danger" onClick={onConfirm}>Cancel Agreement</Button></>}>
        {agreement && (
            <p className="text-sm text-gray-600">
                Cancel the agreement for{' '}
                <span className="font-semibold text-gray-900">{agreement.advisorFirstName} {agreement.advisorLastName}</span>?
                <br /><br />
                The advisor's link will stop working immediately and can no longer be used or submitted. This cannot be undone.
            </p>
        )}
    </Modal>
);

// Shown when an agreement is created but its invite email couldn't be delivered
// (initial send or resend). Lets the user copy the secure link, share it manually,
// and optionally jot a note (e.g. "manually sent link July 10") before dismissing.
const AgreementFailedModal = ({ data, authParams, onSaved, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setNote(''); setCopied(false); }, [data]);

    const copy = async () => { try { await navigator.clipboard.writeText(data.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ } };

    const done = async () => {
        const trimmed = note.trim();
        if (!trimmed) { onClose(); return; }
        setSaving(true);
        try {
            const res = await authPost(`${getApiUrl()}/sales/agreements/${data.id}/note`, { notes: trimmed }, authParams);
            if (!res.ok) { toast.error('Note could not be saved.'); return; }
            onSaved?.();
            onClose();
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Note could not be saved.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={!!data} onClose={onClose} title="Send Agreement" size="md" footer={<Button onClick={done} loading={saving} fullWidth>Done</Button>}>
            {data && (
                <div className="space-y-3">
                    <p className="text-sm font-medium text-red-600">
                        Agreement was created for <span className="font-semibold">{data.firstName} {data.lastName}</span>, but the link could not be sent.
                    </p>
                    <div className="flex items-center gap-2">
                        <input readOnly value={data.link} onFocus={(e) => e.target.select()} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700" />
                        <Button variant="outline" size="sm" onClick={copy}>{copied ? <><Check className="w-4 h-4" />Copied</> : <><Copy className="w-4 h-4" />Copy</>}</Button>
                    </div>
                    <p className="text-sm text-gray-400">
                        Please try again using the dashboard's <span className="font-semibold">Resend</span> button — or if issues persist, manually share this secure link with the advisor (valid 2 weeks).
                    </p>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add a note <span className="font-normal text-gray-400">(optional)</span></label>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000}
                            placeholder="e.g. Manually sent link July 10"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                </div>
            )}
        </Modal>
    );
};

// Add or edit the free-text note on any agreement, opened from the table's Actions column.
const NoteModal = ({ agreement, authParams, onSaved, onClose }) => {
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setNote(agreement?.notes || ''); }, [agreement]);

    const save = async () => {
        setSaving(true);
        try {
            const res = await authPost(`${getApiUrl()}/sales/agreements/${agreement.id}/note`, { notes: note }, authParams);
            if (!res.ok) { toast.error('Failed to save note.'); return; }
            toast.success('Note saved.');
            onSaved?.();
            onClose();
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Failed to save note.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={!!agreement} onClose={onClose} title="Agreement Note" size="md"
            footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} loading={saving}>Save Note</Button></>}>
            {agreement && (
                <div className="space-y-2">
                    <p className="text-sm text-gray-600">Note for <span className="font-semibold text-gray-900">{agreement.advisorFirstName} {agreement.advisorLastName}</span></p>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={1000}
                        placeholder="e.g. Manually sent link July 10"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
                </div>
            )}
        </Modal>
    );
};

const FORM_TYPE_OPTIONS = [
    { value: 'general', label: 'General' },
    { value: 'jg', label: 'JG (Johnston Group)' },
];

const SendAgreementModal = ({ isOpen, onClose, authParams, onCreated, onFailed }) => {
    const [form, setForm] = useState({ formType: 'general', firstName: '', lastName: '', email: '' });
    const [sending, setSending] = useState(false);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const close = () => { setForm({ formType: 'general', firstName: '', lastName: '', email: '' }); setSending(false); onClose(); };

    const handleSend = async () => {
        if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) { toast.error('Please fill in all fields.'); return; }
        setSending(true);
        try {
            const res = await authPost(`${getApiUrl()}/sales/agreements`, {
                formType: form.formType, advisorFirstName: form.firstName, advisorLastName: form.lastName, advisorEmail: form.email,
            }, authParams);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // 502 = agreement created but the email failed; hand off to the failure popup so the link can still be copied/resent
                if (data.secureToken) {
                    onFailed?.({ id: data.id, firstName: form.firstName, lastName: form.lastName, link: `${window.location.origin}/agreement/${data.secureToken}` });
                    onCreated?.();
                    close();
                } else {
                    toast.error(data.message || 'Failed to create agreement.');
                }
                return;
            }
            toast.success(`Agreement sent to ${form.firstName} ${form.lastName}.`);
            onCreated?.();
            close();
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Failed to create agreement.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={close} title="Send Agreement" size="md"
            footer={<><Button variant="secondary" onClick={close}>Cancel</Button><Button onClick={handleSend} loading={sending}><Send className="w-4 h-4" />Send Agreement</Button></>}>
            <div className="space-y-4">
                <Input.Select label="Form Type" value={form.formType} onChange={(e) => set('formType', e.target.value)} options={FORM_TYPE_OPTIONS} placeholder="Select form type" />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="First Name" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="First name" />
                    <Input label="Last Name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Last name" />
                </div>
                <Input label="Advisor Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" />
            </div>
        </Modal>
    );
};

const DashboardPage = () => {
    const { currentUser, onSessionExpired, onTokenRefresh } = useAuth();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const [agreements, setAgreements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resending, setResending] = useState(null);
    const [resendTarget, setResendTarget] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [sendOpen, setSendOpen] = useState(false);
    const [failed, setFailed] = useState(null);
    const [noteTarget, setNoteTarget] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);

    const fetchAgreements = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authGet(`${getApiUrl()}/sales/agreements`, authParams);
            setAgreements(await res.json());
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Failed to load agreements');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    useEffect(() => { fetchAgreements(); }, [fetchAgreements]);

    const handleResend = async () => {
        if (!resendTarget) return;
        const target = resendTarget;
        setResending(target.id);
        setResendTarget(null);
        const id = toast.loading('Sending link...');
        try {
            const res = await authPost(`${getApiUrl()}/sales/agreements/${target.id}/resend`, {}, authParams);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // 502 = email failed; row is now "not_sent". Surface the failure popup with the link.
                if (data.secureToken) {
                    toast.dismiss(id);
                    setFailed({ id: target.id, firstName: target.advisorFirstName, lastName: target.advisorLastName, link: `${window.location.origin}/agreement/${data.secureToken}` });
                } else {
                    toast.error(data.message || 'Failed to resend link', { id });
                }
                fetchAgreements();
                return;
            }
            toast.success('Agreement link resent successfully', { id });
            fetchAgreements();
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Failed to resend link', { id });
        } finally {
            setResending(null);
        }
    };

    const handleCancel = async () => {
        if (!cancelTarget) return;
        const target = cancelTarget;
        setCancelTarget(null);
        const id = toast.loading('Cancelling...');
        try {
            const res = await authPost(`${getApiUrl()}/sales/agreements/${target.id}/cancel`, {}, authParams);
            if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.message || 'Failed to cancel agreement.', { id }); return; }
            toast.success('Agreement cancelled.', { id });
            fetchAgreements();
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) toast.error('Failed to cancel agreement.', { id });
        }
    };

    const counts = agreements.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, { sent: 0, not_sent: 0, draft: 0, signed: 0, expired: 0, cancelled: 0 });

    const filtered = agreements.filter((a) => {
        const matchesFilter = activeFilter === 'all' || a.status === activeFilter;
        const q = search.toLowerCase();
        const matchesSearch = !q || `${a.advisorFirstName} ${a.advisorLastName}`.toLowerCase().includes(q) || a.advisorEmail.toLowerCase().includes(q);
        return matchesFilter && matchesSearch;
    });

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-10 lg:px-12 py-6">
                {/* header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-xl"><LayoutDashboard className="w-6 h-6 text-primary" /></div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Sales Dashboard</h1>
                            <p className="text-sm text-gray-500">Track advisor agreements you've sent</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => setSendOpen(true)}>
                            <Plus className="w-4 h-4" />Send Agreement
                        </Button>
                        <Button variant="secondary" size="sm" onClick={fetchAgreements} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh
                        </Button>
                    </div>
                </div>

                {/* filter + table */}
                <Card padding="none">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-gray-100">
                        <div className="flex items-center gap-1 flex-wrap">
                            {FILTER_TABS.map(tab => (
                                <button key={tab.key} onClick={() => setActiveFilter(tab.key)}
                                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${activeFilter === tab.key ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                                    {tab.label}
                                    {tab.key !== 'all' && counts[tab.key] > 0 && (
                                        <span className={`ml-1.5 text-xs ${activeFilter === tab.key ? 'text-white/80' : 'text-gray-400'}`}>{counts[tab.key]}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="sm:w-64">
                            <Input.Search placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-16 flex justify-center"><Spinner.Overlay message="Loading agreements..." /></div>
                    ) : (
                        <Table>
                            <Table.Header>
                                <Table.Row hoverable={false}>
                                    <Table.Head>Advisor</Table.Head>
                                    <Table.Head>Email</Table.Head>
                                    <Table.Head>Form Type</Table.Head>
                                    <Table.Head>Status</Table.Head>
                                    <Table.Head>Sent</Table.Head>
                                    <Table.Head>Last Updated</Table.Head>
                                    <Table.Head align="right">Actions</Table.Head>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {filtered.length === 0 ? (
                                    <Table.Empty colSpan={7}>
                                        <div className="flex flex-col items-center gap-2">
                                            <Send className="w-8 h-8 text-gray-300" />
                                            <p className="font-medium">No agreements found</p>
                                            <p className="text-xs text-gray-400">{search ? 'Try a different search term' : 'Agreements you send will appear here'}</p>
                                        </div>
                                    </Table.Empty>
                                ) : filtered.map(a => (
                                    <Table.Row key={a.id}>
                                        <Table.Cell>
                                            <span className="font-medium">{a.advisorFirstName} {a.advisorLastName}</span>
                                            {a.notes && (
                                                <span className="flex items-center gap-1 text-xs text-gray-400 mt-0.5 max-w-[220px]" title={a.notes}>
                                                    <StickyNote className="w-3 h-3 shrink-0" /><span className="truncate">{a.notes}</span>
                                                </span>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell className="text-gray-500">{a.advisorEmail}</Table.Cell>
                                        <Table.Cell>
                                            <Badge variant={a.formType === 'jg' ? 'secondary' : 'default'}>{FORM_TYPE_LABELS[a.formType] || a.formType}</Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge variant={STATUS_BADGE[a.status] || 'default'}>{STATUS_LABELS[a.status] || a.status}</Badge>
                                        </Table.Cell>
                                        <Table.Cell className="text-gray-500 whitespace-nowrap">{a.sentAt ? moment(a.sentAt).format('MMM D, YYYY h:mm A') : '—'}</Table.Cell>
                                        <Table.Cell className="text-gray-500 whitespace-nowrap">{a.updatedAt ? moment(a.updatedAt).format('MMM D, YYYY h:mm A') : '—'}</Table.Cell>
                                        <Table.Cell align="right">
                                            <div className="flex items-center justify-end gap-3">
                                                {a.secureToken && ACTIVE_STATUSES.includes(a.status) && (
                                                    <a href={`/agreement/${a.secureToken}`} target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                                                        <ExternalLink className="w-3.5 h-3.5" />Open form
                                                    </a>
                                                )}
                                                {ACTIVE_STATUSES.includes(a.status) && (
                                                    <Button variant="tertiary" size="sm" loading={resending === a.id} onClick={() => setResendTarget(a)}>
                                                        <Send className="w-3.5 h-3.5" />Resend
                                                    </Button>
                                                )}
                                                <Button variant="tertiary" size="sm" onClick={() => setNoteTarget(a)} title={a.notes ? 'Edit note' : 'Add note'}>
                                                    <StickyNote className="w-3.5 h-3.5" />{a.notes ? 'Note' : 'Add note'}
                                                </Button>
                                                {ACTIVE_STATUSES.includes(a.status) && (
                                                    <Button variant="tertiary" size="sm" className="!text-red-600" onClick={() => setCancelTarget(a)} title="Cancel agreement">
                                                        <Ban className="w-3.5 h-3.5" />Cancel
                                                    </Button>
                                                )}
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </Card>
            </div>

            <ResendModal agreement={resendTarget} onConfirm={handleResend} onClose={() => setResendTarget(null)} />
            <CancelModal agreement={cancelTarget} onConfirm={handleCancel} onClose={() => setCancelTarget(null)} />
            <SendAgreementModal isOpen={sendOpen} onClose={() => setSendOpen(false)} authParams={authParams} onCreated={fetchAgreements} onFailed={setFailed} />
            <AgreementFailedModal data={failed} authParams={authParams} onSaved={fetchAgreements} onClose={() => setFailed(null)} />
            <NoteModal agreement={noteTarget} authParams={authParams} onSaved={fetchAgreements} onClose={() => setNoteTarget(null)} />
        </PageWrapper>
    );
};

export default DashboardPage;
